import { useState, useCallback } from "react";
import {
  _get_notification_rules,
  _get_notification_rule,
  _create_notification_rule,
  _update_notification_rule,
  _toggle_notification_rule,
  _get_notification_preferences,
  _update_all_notification_preferences,
  _update_notification_preference,
  _get_all_notification_templates,
  _get_notification_templates,
  _get_notification_template,
  _update_notification_template,
  _preview_notification_template,
  _get_notification_variables,
} from "../../../common/axios_services";

const useNotificationData = () => {
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateVariables, setTemplateVariables] = useState([]);
  const [preferences, setPreferences] = useState([]);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [allTemplates, setAllTemplates] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRules = useCallback(async () => {
    try {
      setLoading(true);
      const res = await _get_notification_rules();
      setRules(res?.data?.data || []);
      return { success: true };
    } catch (err) {
      setError(err.message || "Failed to load rules");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRule = useCallback(async (eventType) => {
    try {
      setLoading(true);
      const res = await _get_notification_rule(eventType);
      return { success: true, data: res?.data?.data };
    } catch (err) {
      setError(err.message || "Failed to load rule");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const createRule = useCallback(async (data) => {
    try {
      setLoading(true);
      const res = await _create_notification_rule(data);
      await fetchRules();
      return { success: true, data: res?.data?.data };
    } catch (err) {
      setError(err.message || "Failed to create rule");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateRule = useCallback(async (eventType, data) => {
    try {
      setLoading(true);
      const res = await _update_notification_rule(eventType, data);
      await fetchRules();
      return { success: true };
    } catch (err) {
      setError(err.message || "Failed to update rule");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleRule = useCallback(async (eventType, isActive) => {
    try {
      setLoading(true);
      const res = await _toggle_notification_rule(eventType, isActive);
      await fetchRules();
      return { success: true };
    } catch (err) {
      setError(err.message || "Failed to toggle rule");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPreferences = useCallback(async (userId) => {
    try {
      setLoading(true);
      const res = await _get_notification_preferences(userId);
      setPreferences(res?.data?.data || []);
      return { success: true };
    } catch (err) {
      setError(err.message || "Failed to load preferences");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateAllPreferences = useCallback(async (userId, data) => {
    try {
      setLoading(true);
      const res = await _update_all_notification_preferences(userId, data);
      await fetchPreferences(userId);
      return { success: true };
    } catch (err) {
      setError(err.message || "Failed to update preferences");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const updatePreference = useCallback(async (userId, channel, isOptedIn) => {
    try {
      setLoading(true);
      const res = await _update_notification_preference(
        userId,
        channel,
        isOptedIn,
      );
      await fetchPreferences(userId);
      return { success: true };
    } catch (err) {
      setError(err.message || "Failed to update preference");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAllTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const [templRes, rulesRes] = await Promise.all([
        _get_all_notification_templates(),
        _get_notification_rules(),
      ]);

      const templatesData = templRes?.data?.data || [];
      const rulesData = rulesRes?.data?.data || [];

      const filteredTemplates = templatesData
        .filter((t) => t?.rule?.isActive !== false)
        .sort((a, b) => a.eventType.localeCompare(b.eventType));

      setAllTemplates(filteredTemplates);

      const ruleEventTypes = rulesData
        .filter((r) => r.isActive !== false)
        .map((r) => r.eventType);

      const uniqueEventTypes = [...new Set([...ruleEventTypes])];

      const uniqueChannels = [...new Set(filteredTemplates.map((t) => t.channel))];

      setEventTypes(uniqueEventTypes.sort());
      setChannels(
        uniqueChannels.length > 0
          ? uniqueChannels.sort()
          : ["PUSH", "EMAIL", "SMS", "WHATSAPP"],
      );

      return { success: true, data: filteredTemplates };
    } catch (err) {
      setError(err.message || "Failed to load all templates");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTemplates = useCallback(async (eventType) => {
    try {
      setLoading(true);
      const res = await _get_notification_templates(eventType);
      setTemplates(res?.data?.data || []);
      return { success: true };
    } catch (err) {
      setError(err.message || "Failed to load templates");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateTemplate = useCallback(async (eventType, channel, data) => {
    try {
      setLoading(true);
      const res = await _update_notification_template(eventType, channel, data);
      await fetchTemplates(eventType);
      return { success: true };
    } catch (err) {
      setError(err.message || "Failed to update template");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const previewTemplate = useCallback(async (eventType, channel, variables) => {
    try {
      setLoading(true);
      const res = await _preview_notification_template(
        eventType,
        channel,
        variables,
      );
      setTemplatePreview(res?.data?.data);
      return { success: true, data: res?.data?.data };
    } catch (err) {
      setError(err.message || "Failed to preview template");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchVariables = useCallback(async () => {
    try {
      setLoading(true);
      const res = await _get_notification_variables();
      setTemplateVariables(res?.data?.data || []);
      return { success: true, data: res?.data?.data };
    } catch (err) {
      setError(err.message || "Failed to load variables");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    rules,
    templates,
    preferences,
    templatePreview,
    allTemplates,
    eventTypes,
    channels,
    templateVariables,
    loading,
    error,
    fetchRules,
    fetchRule,
    createRule,
    updateRule,
    toggleRule,
    fetchPreferences,
    updateAllPreferences,
    updatePreference,
    fetchAllTemplates,
    fetchTemplates,
    updateTemplate,
    previewTemplate,
    fetchVariables,
  };
};

export default useNotificationData;
